import { Fab } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import { _t } from '@/locale';
import { WeekHeaderProps } from './WeekHeader.types';

/**
 * Shared week-range header for the weekpicker content views (MPA-2580 loading,
 * MPA-2581+ loaded calendar/week/month). Renders the centered week range with a
 * navigation arrow on either side.
 *
 * Figma: Iteration 10 — node 502-19700 (desktop) / 502-19769 (narrow).
 *
 * Built as a shared surface from the start because every pick-time view reuses
 * it; the loading view disables both arrows so they hold their space and the
 * hand-off to the loaded view shifts layout no more than 4 px. It does not
 * set its own background — the surrounding content view owns the surface.
 */
const WeekHeader = ({
  title,
  subtitle,
  isNarrow = false,
  prevDisabled = false,
  nextDisabled = false,
  onPrev,
  onNext,
}: WeekHeaderProps) => {
  const navButton = (direction: 'previous' | 'next') => {
    const isPrevious = direction === 'previous';
    const isDisabled = isPrevious ? prevDisabled : nextDisabled;
    const onNavigate = isPrevious ? onPrev : onNext;
    return (
      <Fab
        size="sm"
        variant="secondary"
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : undefined}
        onClick={isDisabled ? undefined : onNavigate}
        icon={
          <Icon variant={isPrevious ? 'arrow-left' : 'arrow-right'} color={isDisabled ? 'fg_disabled' : 'fg_primary'} />
        }
        aria-label={_t(isPrevious ? 'features.weekpicker.previousWeek' : 'features.weekpicker.nextWeek')}
      />
    );
  };

  return (
    <div className={classnames('flex w-full items-center justify-between', isNarrow ? 'px-md py-sm' : 'px-xl py-lg')}>
      {navButton('previous')}
      <div className={classnames('flex flex-col items-center text-center', isNarrow && 'gap-2xs')}>
        <p className={classnames('text-fg_primary font-bold', isNarrow ? 'text-l' : 'text-xl')}>{title}</p>
        {subtitle && <p className={classnames('text-fg_secondary', isNarrow ? 'text-s' : 'text-m')}>{subtitle}</p>}
      </div>
      {navButton('next')}
    </div>
  );
};

export default WeekHeader;
